import { z } from 'zod';
/** Matches Prisma's ArtifactReviewStatus enum — shared by Credential
 *  and ComplianceArtifact since the review lifecycle is identical. */
export const ArtifactReviewStatusSchema = z.enum([
    'PENDING',
    'APPROVED',
    'REJECTED',
    'EXPIRED',
]);
/** Professional + identity credentials — rarely changes after capture.
 *  Renewable compliance items (TB, vaccination, GL insurance, WSIB) live
 *  in the ComplianceArtifact table; see compliance.schema.ts. */
export const CredentialTypeSchema = z.enum([
    'NACC', // PSW (Q11)
    'CNO_REGISTRATION', // RPN / RN (Q11)
    'VSC', // Vulnerable Sector Check (Q11)
    'GOVERNMENT_ID',
    'DSW_CREDENTIAL', // Developmental Support Worker — generic value; display label finalized later
    'FOOD_HANDLER', // Cook / Dietary Aide — "Food Handler Licence" (role-gating, Phase 17)
    'CPR', // Mandatory for RN / RPN / PSW / DSW — "CPR certification" (role-gating, 2026-08 revamp)
]);
/** Image / PDF MIME types accepted for credential scans. */
const AllowedUploadContentTypeSchema = z
    .string()
    .regex(/^(image\/(jpeg|png|webp)|application\/pdf)$/, 'Only JPEG, PNG, WebP, or PDF allowed');
/** Max upload size for credential scans — 20 MB.
 *  Aligned with object storage signed-URL Content-Length cap; oversized
 *  files would fail at the S3 PUT step regardless. */
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
/** POST /api/v1/caregivers/me/credentials/upload-url body.
 *  Two-step upload pattern: client asks the API for a presigned S3 URL,
 *  PUTs the file directly to object storage, then POSTs to /credentials
 *  with the resulting storage key to record the credential. */
export const CredentialUploadUrlRequestSchema = z.object({
    type: CredentialTypeSchema,
    contentType: AllowedUploadContentTypeSchema,
    contentLength: z.number().int().positive().max(MAX_UPLOAD_BYTES),
});
export const CredentialUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    objectStorageKey: z.string().min(1).max(500),
    expiresAt: z.string().datetime(),
});
/** POST /api/v1/caregivers/me/credentials body.
 *  Records a credential after the PUT to S3 has completed. The storage
 *  key must match a key issued by /upload-url in this same request flow
 *  (the server validates ownership + ttl).
 *
 *  Multiple rows of the same `type` are allowed — re-uploading a credential
 *  (e.g. after a VSC refresh) creates a new row; the old row's status
 *  flips to EXPIRED by the Phase 13/15 batch job once `expiresAt` passes.
 *  Operator review queue surfaces the new PENDING row. */
export const CredentialRecordRequestSchema = z.object({
    type: CredentialTypeSchema,
    externalReferenceNumber: z.string().max(120).trim().nullable().optional(),
    issuedAt: z.string().date().nullable().optional(),
    expiresAt: z.string().date().nullable().optional(),
    objectStorageKey: z.string().min(1).max(500),
});
/** Shape returned for a credential — used by /caregivers/me responses
 *  and by the admin review-queue endpoints. */
export const CredentialResponseSchema = z.object({
    id: z.string().uuid(),
    type: CredentialTypeSchema,
    status: ArtifactReviewStatusSchema,
    externalReferenceNumber: z.string().nullable(),
    issuedAt: z.string().date().nullable(),
    expiresAt: z.string().date().nullable(),
    uploadedAt: z.string().datetime().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Phase 5f-2 + 5f-b — catalog item shape for the data-driven
 *  credentials section in the caregiver portal.
 *
 *  The catalog tells the portal:
 *    - Which credential types this caregiver needs to upload (driven
 *      by their declaredRoleCodes — single source of truth in
 *      activation-requirements.ts).
 *    - The current credential row of each required type (or null if
 *      never uploaded) so the portal can show status + expiry.
 *    - The per-type extra fields the portal should render alongside
 *      the upload button (e.g., NACC/CNO/VSC have an
 *      externalReferenceNumber field; Government ID does not).
 *
 *  Adding a new credential type to the activation matrix means
 *  extending the CredentialType enum + the catalog method on the
 *  backend; the portal renders the new row with zero code changes
 *  (per 5f-2 lock). */
export const CredentialCatalogItemSchema = z.object({
    type: CredentialTypeSchema,
    /** User-facing label rendered above the upload button. */
    label: z.string(),
    /** Always `true` in v1 (the catalog filters to required types only).
     *  Field retained so the schema scales to optional types later
     *  without a breaking API change. */
    requiredForCaregiver: z.boolean(),
    /** Most-recent credential row of this type, if any. Status +
     *  expiresAt + externalReferenceNumber drive the row UX. */
    currentArtifact: z
        .object({
        id: z.string().uuid(),
        status: ArtifactReviewStatusSchema,
        expiresAt: z.string().date().nullable(),
        externalReferenceNumber: z.string().nullable(),
        /** Audit H15 (2026-09-01) — the operator-supplied reason when status is
         *  REJECTED, so the caregiver knows what to fix. Null unless REJECTED. */
        rejectionNotes: z.string().nullable(),
    })
        .nullable(),
    /** Per-type extra input fields the portal renders. E.g.,
     *  `['externalReferenceNumber']` for NACC/CNO/VSC. Empty for
     *  types where no identifying number applies (Government ID). */
    extraFields: z.array(z.string()),
});
export const CredentialCatalogResponseSchema = z.object({
    items: z.array(CredentialCatalogItemSchema),
});
//# sourceMappingURL=credential.schema.js.map